import React, { FC, useCallback, useMemo } from 'react';

import { Divider, Typography } from '@mui/material';
import { FormikProps } from 'formik';
import { Box, Stack } from '@mui/system';

import {
  AdminAllowanceNames,
  AllowancesNames,
  ContractTypes,
  convertToDecimals,
  currency,
  decimalMaxLength,
  getLongTermContractAttributes,
} from '@pharmaplan/common';
import { getDaysDistribution } from '@pharmaplan/common/helpers/utils';

import { useAppSelector } from '../../../../../hooks/useAppSelector';
import CustomTextField from '../../../../common/CustomTextField';
import { Constants } from '../../../../../helpers/Constants';
import strings from '../../../../../localization';

import classes from './styles';

interface ILongTermHourlyRates {
  formik: FormikProps<any>;
  customWeekHours?: number;
  customWeekendHours?: number;
  CustomContent?: React.ReactNode;
  bookedWeekdayCost?: number;
  bookedWeekendCost?: number;
  pharmacyBookedWeekdayCost?: number;
  pharmacyBookedWeekendCost?: number;
  bookedWeekdayHours?: number;
  bookedWeekendHours?: number;
  promoBonusTotal?: number;
  professionalCreditsLeft?: number;
  creditsLeft?: number;
  hourlyBonus?: number;
  promoTotal?: number;
}

const LongTermHourlyRates: FC<ILongTermHourlyRates> = ({
  bookedWeekdayHours = 0,
  bookedWeekendHours = 0,
  pharmacyBookedWeekdayCost = 0,
  pharmacyBookedWeekendCost = 0,
  professionalCreditsLeft,
  customWeekendHours,
  bookedWeekdayCost = 0,
  bookedWeekendCost = 0,
  customWeekHours,
  promoBonusTotal = 0,
  CustomContent,
  hourlyBonus,
  creditsLeft,
  promoTotal,
  formik,
}) => {
  const longTermAttributes = useAppSelector(getLongTermContractAttributes);
  const { startDate, endDate, shiftContractAttributes } = longTermAttributes ?? {};

  const {
    weekDayPaidHourlyRate,
    weekendPaidHourlyRate,
    pharmacyWeekDayHourlyRate,
    pharmacyWeekendHourlyRate,
  } = formik.values ?? {};

  const { weekdayHours, weekendHours } = useMemo(() => {
    if (
      (customWeekHours || customWeekHours === 0)
      && (customWeekendHours || customWeekendHours === 0)
    ) {
      return {
        weekdayHours: customWeekHours,
        weekendHours: customWeekendHours,
      };
    }
    return getDaysDistribution(shiftContractAttributes);
  }, [customWeekHours, customWeekendHours, shiftContractAttributes]);

  const weekdayCalcHours = weekdayHours - bookedWeekdayHours;
  const weekendCalcHours = weekendHours - bookedWeekendHours;
  const weekdayTotalRate = weekDayPaidHourlyRate * weekdayHours - bookedWeekdayCost;
  const weekendTotalRate = weekendPaidHourlyRate * weekendHours - bookedWeekendCost;
  const pharmacyWeekdayTotalRate = pharmacyWeekDayHourlyRate * weekdayHours - pharmacyBookedWeekdayCost;
  const pharmacyWeekendTotalRate = pharmacyWeekendHourlyRate * weekendHours - pharmacyBookedWeekendCost;
  const totalBookedCost = bookedWeekdayCost + bookedWeekendCost;
  const totalPharmacyBookedCost = (pharmacyBookedWeekdayCost) + pharmacyBookedWeekendCost;

  const professionalTotal = `${currency}${convertToDecimals(
    weekdayTotalRate + weekendTotalRate + totalBookedCost + promoBonusTotal,
  )}`;

  const pharmacyTotal = `${currency}${convertToDecimals(
    pharmacyWeekdayTotalRate
      + pharmacyWeekendTotalRate
      + totalPharmacyBookedCost
      - (promoTotal ?? 0),
  )} + Tx ${strings.and} ${strings.otherFees}`;

  const showCalculation = startDate && endDate;
  const showWeekday = weekDayPaidHourlyRate && weekdayHours;
  const showWeekend = weekendPaidHourlyRate && weekendHours;

  const showPharmacyWeekday = pharmacyWeekDayHourlyRate && weekdayHours;
  const showPharmacyWeekend = pharmacyWeekendHourlyRate && weekendHours;

  const fieldConfig = [
    {
      label: strings.formatString(
        strings.userWeekdayRate,
        strings.professional,
      ),
      name: AllowancesNames.weekDayPaidHourlyRate,
      id: AllowancesNames.weekDayPaidHourlyRate,
      disabled: !weekdayCalcHours,
    },
    {
      label: strings.formatString(
        strings.userWeekendRate,
        strings.professional,
      ),
      name: AllowancesNames.weekendPaidHourlyRate,
      id: AllowancesNames.weekendPaidHourlyRate,
      disabled: !weekendCalcHours,
    },

    {
      label: strings.formatString(strings.userWeekdayRate, strings.pharmacy),
      name: AdminAllowanceNames.pharmacyWeekDayHourlyRate,
      id: AdminAllowanceNames.pharmacyWeekDayHourlyRate,
      disabled: !weekdayCalcHours,
    },
    {
      label: strings.formatString(strings.userWeekendRate, strings.pharmacy),
      name: AdminAllowanceNames.pharmacyWeekendHourlyRate,
      id: AdminAllowanceNames.pharmacyWeekendHourlyRate,
      disabled: !weekendCalcHours,
    },
    creditsLeft && {
      label: strings.promoHourlyRate,
      name: AdminAllowanceNames.promoHourlyRate,
      id: AdminAllowanceNames.promoHourlyRate,
      disabled: true,
      labelStyle: classes.promoText,
      infoText: strings.formatString(
        strings.promotionalCreditWillBeApplied,
        creditsLeft,
      ) as string,
    },
    professionalCreditsLeft && {
      label: strings.promoHourlyBonus,
      name: AdminAllowanceNames.hourlyRateBonus,
      id: AdminAllowanceNames.hourlyRateBonus,
      disabled: true,
      labelStyle: classes.promoText,
      infoText: strings.formatString(
        strings.promotionalBonusWillBeApplied,
        `${hourlyBonus ?? 0}`,
        `${professionalCreditsLeft}`,
      ) as string,
    },
  ].filter(Boolean) as Array<any>;

  const calculationConfig = [
    {
      label: strings.formatString(strings.userWeekday, strings.professional),
      value: `${currency}${weekDayPaidHourlyRate} X ${weekdayCalcHours} = ${currency}${convertToDecimals(
        weekdayTotalRate,
      )}`,
      show: !!showWeekday,
    },
    {
      label: strings.formatString(strings.userWeekend, strings.professional),
      value: `${currency}${weekendPaidHourlyRate} X ${weekendCalcHours} = ${currency}${convertToDecimals(
        weekendTotalRate,
      )}`,
      show: !!showWeekend,
    },
  ];

  const pharmacyCalculationConfig = [
    {
      label: strings.formatString(strings.userWeekday, strings.pharmacy),
      value: `${currency}${pharmacyWeekDayHourlyRate} X ${weekdayCalcHours} = ${currency}${convertToDecimals(
        pharmacyWeekdayTotalRate,
      )}`,
      show: !!showPharmacyWeekday,
    },
    {
      label: strings.formatString(strings.userWeekend, strings.pharmacy),
      value: `${currency}${pharmacyWeekendHourlyRate} X ${weekendCalcHours} = ${currency}${convertToDecimals(
        pharmacyWeekendTotalRate,
      )}`,
      show: !!showPharmacyWeekend,
    },
  ];

  const renderConfig = useCallback(
    (config: typeof calculationConfig) =>
      config.map((item) => {
        const { label, value, show } = item ?? {};

        return (
          show && (
            <Box key={value} sx={classes.weekContainer}>
              <Typography sx={classes.label}>{label}</Typography>
              <Typography sx={classes.rateValue}>{value}</Typography>
            </Box>
          )
        );
      }),
    [],
  );

  if (formik.values.contractType === ContractTypes.shortTerm) return null;

  return (
    <Stack>
      <Typography sx={classes.title}>{strings.rateAndAllowances}</Typography>

      {fieldConfig.map((item) => {
        const { label, name, id, disabled, infoText, labelStyle } = item;
        return (
          <CustomTextField
            key={id}
            label={label as string}
            name={name}
            infoText={infoText}
            formik={formik}
            disabled={disabled}
            placeholder={Constants.allowancePlaceholder}
            suffix={Constants.priceSuffix}
            customLabelStyle={{
              whiteSpace: 'normal',
              ...labelStyle,
            }}
            maxLength={decimalMaxLength}
            customClass={classes.hourlyRate}
            id={id}
            horizontal
          />
        );
      })}

      {CustomContent}

      {showCalculation && (
        <Box sx={classes.calculationContainer}>
          <Typography sx={classes.title}>
            {strings.estimatedShiftCost}
          </Typography>

          {renderConfig(calculationConfig)}

          {!!professionalCreditsLeft && (
            <Box sx={classes.weekContainer}>
              <Typography sx={classes.promoLabel}>{strings.bonus}</Typography>
              <Typography sx={classes.promoTotal}>
                +
                {`${currency}${convertToDecimals(promoBonusTotal ?? 0)}`}
              </Typography>
            </Box>
          )}

          {!!totalBookedCost && (
            <Box sx={classes.weekContainer}>
              <Typography sx={classes.label}>
                {strings.bookedContractValue}
              </Typography>
              <Typography sx={classes.rateValue}>
                {`${currency}${convertToDecimals(totalBookedCost)}`}
              </Typography>
            </Box>
          )}
          <Divider sx={classes.divider} />
          <Box sx={classes.weekContainer}>
            <Typography sx={classes.label}>{strings.total}</Typography>
            <Typography sx={classes.rateValue}>{professionalTotal}</Typography>
          </Box>
        </Box>
      )}

      {showCalculation && (
        <Box sx={classes.calculationContainer}>
          {renderConfig(pharmacyCalculationConfig)}

          {!!creditsLeft && (
            <Box sx={classes.weekContainer}>
              <Typography sx={classes.promoLabel}>
                {strings.promoDiscount}
              </Typography>
              <Typography sx={classes.promoTotal}>
                -
                {`${currency}${convertToDecimals(promoTotal)}`}
              </Typography>
            </Box>
          )}

          {!!totalBookedCost && (
            <Box sx={classes.weekContainer}>
              <Typography sx={classes.label}>
                {strings.bookedContractValue}
              </Typography>
              <Typography sx={classes.rateValue}>
                {`${currency}${convertToDecimals(totalPharmacyBookedCost)}`}
              </Typography>
            </Box>
          )}

          <Divider sx={classes.divider} />
          <Box sx={classes.weekContainer}>
            <Typography sx={classes.label}>{strings.total}</Typography>
            <Typography sx={classes.rateValue}>{pharmacyTotal}</Typography>
          </Box>
        </Box>
      )}
    </Stack>
  );
};
export default LongTermHourlyRates;
